import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Eye,
  MoreHorizontal,
  User,
  Mail,
  Phone,
  Calendar,
  Ticket,
  Shield,
  Ban,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  cn,
  formatNaira,
  StatCard,
  useToast,
} from '@raffle/ui';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  role: 'user' | 'admin' | 'super_admin';
  status: 'active' | 'suspended' | 'pending';
  ticketsPurchased: number;
  totalSpent: number;
  joinedDate: Date;
  lastActive: Date;
}

const mockUsers: AdminUser[] = [
  {
    id: 'u1',
    name: 'Adebayo Johnson',
    email: 'adebayo@email.com',
    phone: '+234 801 234 5678',
    role: 'user',
    status: 'active',
    ticketsPurchased: 45,
    totalSpent: 2250000,
    joinedDate: new Date('2024-01-15'),
    lastActive: new Date(Date.now() - 1000 * 60 * 30),
  },
  {
    id: 'u2',
    name: 'Ngozi Okonkwo',
    email: 'ngozi@email.com',
    phone: '+234 802 345 6789',
    role: 'user',
    status: 'active',
    ticketsPurchased: 23,
    totalSpent: 1150000,
    joinedDate: new Date('2024-02-20'),
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2),
  },
  {
    id: 'u3',
    name: 'Chinedu Eze',
    email: 'chinedu@email.com',
    phone: '+234 803 456 7890',
    role: 'admin',
    status: 'active',
    ticketsPurchased: 0,
    totalSpent: 0,
    joinedDate: new Date('2023-11-01'),
    lastActive: new Date(Date.now() - 1000 * 60 * 5),
  },
  {
    id: 'u4',
    name: 'Folake Adeyemi',
    email: 'folake@email.com',
    phone: '+234 804 567 8901',
    role: 'user',
    status: 'suspended',
    ticketsPurchased: 12,
    totalSpent: 600000,
    joinedDate: new Date('2024-03-05'),
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
  },
  {
    id: 'u5',
    name: 'Emeka Nwosu',
    email: 'emeka@email.com',
    phone: '+234 805 678 9012',
    role: 'user',
    status: 'pending',
    ticketsPurchased: 0,
    totalSpent: 0,
    joinedDate: new Date(Date.now() - 1000 * 60 * 60 * 2),
    lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2),
  },
  {
    id: 'u6',
    name: 'System Admin',
    email: 'admin@rafflehub.com',
    phone: '+234 800 000 0000',
    role: 'super_admin',
    status: 'active',
    ticketsPurchased: 0,
    totalSpent: 0,
    joinedDate: new Date('2023-01-01'),
    lastActive: new Date(),
  },
];

const statusColors = {
  active: 'success',
  suspended: 'error',
  pending: 'warning',
} as const;

const roleColors = {
  user: 'default',
  admin: 'info',
  super_admin: 'warning',
} as const;

const roleLabels = {
  user: 'User',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

export const UsersPage: React.FC = () => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionMenu, setActionMenu] = useState<string | null>(null);

  const filteredUsers = mockUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatLastActive = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleSuspend = (user: AdminUser) => {
    toast.success('User Suspended', `${user.name} has been suspended`);
    setActionMenu(null);
  };

  const handleActivate = (user: AdminUser) => {
    toast.success('User Activated', `${user.name} is now active`);
    setActionMenu(null);
  };

  // Stats
  const totalUsers = mockUsers.filter((u) => u.role === 'user').length;
  const activeUsers = mockUsers.filter((u) => u.status === 'active' && u.role === 'user').length;
  const totalRevenue = mockUsers.reduce((sum, u) => sum + u.totalSpent, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">User Management</h1>
          <p className="text-text-secondary">Manage users and their permissions</p>
        </div>
        <Button variant="primary">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={totalUsers}
          change={{ value: 12, isPositive: true }}
          icon={<User className="w-5 h-5" />}
        />
        <StatCard
          title="Active Users"
          value={activeUsers}
          icon={<CheckCircle className="w-5 h-5" />}
        />
        <StatCard
          title="Admin Accounts"
          value={mockUsers.filter((u) => u.role !== 'user').length}
          icon={<Shield className="w-5 h-5" />}
        />
        <StatCard
          title="Total Revenue"
          value={formatNaira(totalRevenue)}
          change={{ value: 8.5, isPositive: true }}
          icon={<Ticket className="w-5 h-5" />}
        />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-surface-2 border border-white/10 rounded-vault text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'pending', label: 'Pending' },
            ]}
            className="w-full sm:w-36"
          />
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Roles' },
              { value: 'user', label: 'Users' },
              { value: 'admin', label: 'Admins' },
              { value: 'super_admin', label: 'Super Admins' },
            ]}
            className="w-full sm:w-36"
          />
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Tickets
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Spent
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredUsers.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-surface-2/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gold-500/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-gold-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{user.name}</p>
                        <p className="text-xs text-text-muted">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={roleColors[user.role] as any}>
                      {roleLabels[user.role]}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={statusColors[user.status] as any} className="capitalize">
                      {user.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-text-muted" />
                      <span className="text-sm font-medium text-text-primary">
                        {user.ticketsPurchased}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm font-medium text-text-primary">
                      {user.totalSpent > 0 ? formatNaira(user.totalSpent) : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-text-muted">{formatLastActive(user.lastActive)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}
                        className="p-2 rounded-vault text-text-muted hover:text-text-primary hover:bg-surface-3 transition-colors"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      {actionMenu === user.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-surface-2 border border-white/10 rounded-vault-lg shadow-xl z-10">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowUserModal(true);
                                setActionMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-3 flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                            <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-3 flex items-center gap-2">
                              <Edit className="w-4 h-4" />
                              Edit User
                            </button>
                            {user.status === 'active' ? (
                              <button
                                onClick={() => handleSuspend(user)}
                                className="w-full px-4 py-2 text-left text-sm text-vault-error hover:bg-surface-3 flex items-center gap-2"
                              >
                                <Ban className="w-4 h-4" />
                                Suspend User
                              </button>
                            ) : (
                              <button
                                onClick={() => handleActivate(user)}
                                className="w-full px-4 py-2 text-left text-sm text-vault-success hover:bg-surface-3 flex items-center gap-2"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Activate User
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/5">
          <p className="text-sm text-text-muted">
            Showing <span className="font-medium text-text-primary">1</span> to{' '}
            <span className="font-medium text-text-primary">{filteredUsers.length}</span> of{' '}
            <span className="font-medium text-text-primary">{filteredUsers.length}</span> results
          </p>
          <div className="flex gap-2">
            <button className="p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-primary disabled:opacity-50" disabled>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button className="px-3 py-2 rounded-vault bg-gold-500 text-black text-sm font-medium">1</button>
            <button className="p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-primary disabled:opacity-50" disabled>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setShowUserModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-surface-1 rounded-vault-lg border border-white/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gold-500/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-gold-500" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-text-primary">
                    {selectedUser.name}
                  </h2>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={roleColors[selectedUser.role] as any}>
                      {roleLabels[selectedUser.role]}
                    </Badge>
                    <Badge variant={statusColors[selectedUser.status] as any} className="capitalize">
                      {selectedUser.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 text-text-muted mb-1">
                    <Mail className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Email</span>
                  </div>
                  <p className="text-sm text-text-primary">{selectedUser.email}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-text-muted mb-1">
                    <Phone className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Phone</span>
                  </div>
                  <p className="text-sm text-text-primary">{selectedUser.phone}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-text-muted mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Joined</span>
                  </div>
                  <p className="text-sm text-text-primary">{formatDate(selectedUser.joinedDate)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-text-muted mb-1">
                    <Ticket className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Tickets</span>
                  </div>
                  <p className="text-sm text-text-primary">{selectedUser.ticketsPurchased}</p>
                </div>
              </div>

              <div className="p-4 rounded-vault bg-gold-500/10 border border-gold-500/20">
                <p className="text-xs text-text-muted mb-1">Total Spent</p>
                <p className="font-mono text-2xl font-bold text-gold-500">
                  {formatNaira(selectedUser.totalSpent)}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button variant="outline" className="flex-1">
                View Transactions
              </Button>
              <Button variant="ghost" onClick={() => setShowUserModal(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
